import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useMetroConfigSuspense, clearMetroConfigCache } from '../services/useMetroConfigSuspense';
import { isUserAuthenticated } from '../components/verification/verificationApi';
import { config } from '../services/AxiosClient';
import { clearStaleCaches, getUserData } from '../services/userData';

interface MetroConfig {
  // allow null since initial state sets data: null
  data?: unknown;
  docId?: string;
  isLoaded?: boolean;
  loading?: boolean;
  error?: string | null;
  headerLogoUrl?: string;
  headerLogoReady?: boolean;
  source?: 'cache' | 'api' | 'initial'; // where current data came from
}

interface MetroConfigContextType {
  metroConfig: MetroConfig;
  setMetroConfig: (config: MetroConfig) => void;
  clearMetroConfig: () => void;
  retry: () => void;
  isInitializing: boolean;
  docId?: string | null;
}

const MetroConfigContext = createContext<MetroConfigContextType | undefined>(undefined);

export const useMetroConfig = (): MetroConfigContextType => {
  const context = useContext(MetroConfigContext);
  if (!context) {
    throw new Error('useMetroConfig must be used within a MetroConfigProvider');
  }
  return context;
};

interface MetroConfigProviderProps {
  children: ReactNode;
}

// Module-scope helpers to keep effects simple and avoid unstable deps
const toAbsoluteUrl = (maybeRelativePath?: string | null): string | undefined => {
  if (!maybeRelativePath || typeof maybeRelativePath !== 'string') return undefined;
  const API_BASEURL = config?.VITE_API_BASEURL ?? import.meta.env.VITE_API_BASEURL ?? '';
  if (!API_BASEURL) return undefined;
  const base = API_BASEURL.replace(/\/$/, '');
  const norm = maybeRelativePath.replace(/\\/g, '/');
  const path = norm.replace(/^\/+/, '');
  return `${base}/${path}`;
};

const isImagePath = (val: string): boolean => /\.(png|jpe?g|svg)$/i.test(val) || /images\//i.test(val);

const extractLogoPath = (data: unknown): string | undefined => {
  if (!data) return undefined;

  const checkObjectForLogo = (obj: Record<string, unknown>): string | undefined => {
    const keys = ['configValue', 'logo', 'headerLogo'];
    for (const key of keys) {
      if (typeof obj[key] === 'string' && isImagePath(obj[key])) {
        return obj[key];
      }
    }
    return undefined;
  };

  const visitArray = (arr: unknown[]): string | undefined => {
    for (const item of arr) {
      const found = visit(item);
      if (found) return found;
    }
    return undefined;
  };

  const visitObject = (obj: Record<string, unknown>): string | undefined => {
    const directLogo = checkObjectForLogo(obj);
    if (directLogo) return directLogo;
    for (const key of Object.keys(obj)) {
      const found = visit(obj[key]);
      if (found) return found;
    }
    return undefined;
  };

  const visit = (node: unknown): string | undefined => {
    if (!node) return undefined;
    if (typeof node === 'string') return isImagePath(node) ? node : undefined;
    if (Array.isArray(node)) return visitArray(node);
    if (typeof node === 'object') return visitObject(node as Record<string, unknown>);
    return undefined;
  };

  return visit(data);
};

export const MetroConfigProvider: React.FC<MetroConfigProviderProps> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Check if we're on the verification page (before authentication)
  // Support dynamic docId as first segment: /:docId (no other top-level route collisions expected)
  const pathSeg = location.pathname.replace(/^\//, '').split('/')[0];
  const isVerificationPage =
    location.pathname === '/' ||
    location.pathname === '/verify' ||
    (!!pathSeg && !['welcome', 'agent-affiliation', 'support', 'error', 'test'].includes(pathSeg));
  
  // Derive docId from URL first, then fallback to stored user-data
  const deriveDocId = useCallback((): string | null => {
    // Prefer path segment (/:docId) if we are on verification page and segment is not one of reserved routes
    const reserved = new Set(['', 'verify', 'welcome', 'agent-affiliation', 'support', 'error', 'test']);
    let fromUrl: string | null = null;
    if (isVerificationPage && !reserved.has(pathSeg)) {
  fromUrl = pathSeg ?? null;
    }
    if (fromUrl) {
      console.debug('[MetroConfig] docId derived from URL param:', fromUrl);
      return fromUrl;
    }
    try {
      const raw = localStorage.getItem('user-data');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
  const direct = parsed?.docId ?? parsed?.data?.docId ?? parsed?.body?.data?.docId;
      return direct;
    } catch {
      return null;
    }
  // 'searchParams' intentionally excluded: deriveDocId only depends on path segment & verification state.
  }, [isVerificationPage, pathSeg]);

  const [docIdForConfig, setDocIdForConfig] = useState<string | null>(() => deriveDocId());

  // After authentication, check if metro-config-cache exists
  useEffect(() => {
    // Only check after authentication and not on verification pages
    if (isVerificationPage || !isUserAuthenticated()) return;

    const hasMetroCache = localStorage.getItem('metro-config-cache');
  const currentDocId = docIdForConfig ?? searchParams.get('docId');

    if (!hasMetroCache && currentDocId) {
      console.log('[MetroConfig] No metro-config-cache found after authentication, clearing localStorage and redirecting');
      
      // Clear entire localStorage
      localStorage.clear();
      
      // Redirect to index page with same docId
      navigate(`/?docId=${encodeURIComponent(currentDocId)}`);
    }
  }, [isVerificationPage, docIdForConfig, searchParams, navigate]);

  // Watch for changes in search params to update docId
  useEffect(() => {
    const next = deriveDocId();
    setDocIdForConfig(prev => (prev === next ? prev : next));
    if (!next) {
      console.debug('[MetroConfig] No docId derivable from URL or user-data localStorage at this time');
    }
  }, [deriveDocId]);

  const [metroConfig, setMetroConfig] = useState<MetroConfig>(() => {
    const isAuthenticated = isUserAuthenticated();

    const buildBase = (overrides: Partial<MetroConfig> = {}): MetroConfig => ({
      data: null,
  docId: docIdForConfig ?? undefined,
      isLoaded: false,
      loading: !isAuthenticated && !!docIdForConfig,
      error: null,
      headerLogoUrl: undefined,
      headerLogoReady: false,
      source: 'initial',
      ...overrides
    });

    const parseCache = (): { data?: unknown; docId?: string } | undefined => {
      try {
        const raw = localStorage.getItem('metro-config-cache');
        if (!raw) return undefined;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return undefined;

        // Legacy shape
        if ('data' in parsed) {
          const legacy = parsed as { data?: unknown; docId?: string };
          return { data: legacy.data, docId: legacy.docId };
        }

        // Determine target docId (legacy root docId or keyed document map)
        const parsedObj = parsed as Record<string, unknown>;
        const inputDocId = docIdForConfig ?? (typeof parsedObj.docId === 'string' ? parsedObj.docId : undefined);
        if (inputDocId && parsedObj[inputDocId]) {
          const entry = parsedObj[inputDocId];
          if (entry && typeof entry === 'object') {
            const entryObj = entry as { data?: unknown };
            return { data: entryObj.data ?? entry, docId: inputDocId };
          }
        }

        // If only one key with object value, assume it's the doc
        const keys = Object.keys(parsedObj).filter(k => parsedObj[k] && typeof parsedObj[k] === 'object');
        if (!inputDocId && keys.length === 1) {
          const sole = keys[0];
            const entry = parsedObj[sole];
            if (entry && typeof entry === 'object') {
              const entryObj = entry as { data?: unknown };
              return { data: entryObj.data ?? entry, docId: sole };
            }
        }
      } catch (e) {
        console.warn('[MetroConfig] Failed to parse metro-config-cache during initialization:', e);
      }
      return undefined;
    };

    const cacheEntry = parseCache();
    if (!cacheEntry?.data) return buildBase();

    const logoPath = extractLogoPath(cacheEntry.data);
    console.log('[MetroConfig] Initial hydration from metro-config-cache', { docId: cacheEntry.docId });
    return buildBase({
      data: cacheEntry.data,
      docId: (cacheEntry.docId ?? docIdForConfig) ?? undefined,
      isLoaded: true,
      loading: false,
      headerLogoUrl: toAbsoluteUrl(logoPath),
      source: 'cache'
    });
  });

  // Helper to check if we need to update state from cache
  const needsStateUpdate = useCallback((parsed: { data?: unknown; docId?: string }, targetDocId: string) => {
    return !metroConfig.isLoaded || 
        metroConfig.docId !== (parsed.docId ?? targetDocId) ||
           !metroConfig.data;
  }, [metroConfig.isLoaded, metroConfig.docId, metroConfig.data]);

  // Helper to update state from cached data
  const updateFromCache = useCallback((parsed: { data?: unknown; docId?: string }, targetDocId: string) => {
    console.log('[MetroConfig] Loading cached data for authenticated user');
    const logoPath = extractLogoPath(parsed.data);
    setMetroConfig({
      data: parsed.data,
      docId: parsed.docId ?? targetDocId,
      isLoaded: true,
      loading: false,
      error: null,
      headerLogoUrl: toAbsoluteUrl(logoPath),
      headerLogoReady: false,
    });
  }, []);

  // Helper to handle cache loading for authenticated users
  const handleCacheLoad = useCallback((targetDocId: string) => {
    try {
      const cachedData = localStorage.getItem('metro-config-cache');
      if (cachedData) {
        const parsed = JSON.parse(cachedData);
        if (parsed?.data && needsStateUpdate(parsed, targetDocId)) {
          updateFromCache(parsed, targetDocId);
          return true; // Cache loaded successfully
        }
      }
    } catch (e) {
      console.warn('[MetroConfig] Failed to parse cached data:', e);
    }
    return false; // Cache loading failed or not needed
  }, [needsStateUpdate, updateFromCache]);

  // React to docId changes & attempt cache hydration (post-mount updates)
  useEffect(() => {
    if (!docIdForConfig) return;

    // If already loaded for this docId, do nothing
    if (metroConfig.isLoaded && metroConfig.docId === docIdForConfig) return;

    // Authenticated path: we ONLY want cache, never API here.
    if (isUserAuthenticated() && !isVerificationPage) {
      const loaded = handleCacheLoad(docIdForConfig);
      if (!loaded) {
        console.debug('[MetroConfig] Cache not available for authenticated user; marking loaded=false but not triggering API');
  setMetroConfig(prev => ({ ...prev, docId: docIdForConfig, loading: false, isLoaded: false, source: prev.source ?? 'initial' }));
      }
      return; // prevent API path entirely
    }

    // Pre-auth (verification page) path: prepare for API load if needed
    setMetroConfig(prev => ({ ...prev, docId: docIdForConfig, loading: true, error: null }));
  }, [docIdForConfig, metroConfig.isLoaded, metroConfig.docId, handleCacheLoad, isVerificationPage]);

  // Use the suspense hook for API calls - force refresh on verification page
  // After authentication, completely skip API calls - only call on verification page
  const userAuthed = isUserAuthenticated();
  const shouldCallApi = isVerificationPage && !userAuthed;
  const effectiveDocId = shouldCallApi ? docIdForConfig : null;
  const apiState = useMetroConfigSuspense(effectiveDocId, isVerificationPage);
  const [isInitializing, setIsInitializing] = useState(!!docIdForConfig); // Start as initializing if we have docId

  useEffect(() => {
    // If no docId and we already have loaded config, avoid wiping it out
    if (!docIdForConfig && metroConfig.isLoaded && apiState.docId === null && apiState.data === null) {
      if (isInitializing) setIsInitializing(false);
      return;
    }

    // For verification page or when no cache, use API state
    const logoPath = extractLogoPath(apiState.data);

    setMetroConfig(prevConfig => {
      // Never overwrite cache-derived config with null API state
      if (!shouldCallApi) return prevConfig; // If API not supposed to run, ignore
      if (!docIdForConfig && prevConfig.isLoaded && apiState.data === null) return prevConfig;

      // If we already have cache (source cache) and API has no data yet, keep cache
      if (prevConfig.source === 'cache' && (!apiState.data && apiState.loading)) return prevConfig;

      return {
        data: apiState.data,
  docId: apiState.docId ?? (docIdForConfig ?? undefined),
        isLoaded: !apiState.loading && !apiState.error && apiState.data != null,
        loading: apiState.loading,
        error: apiState.error,
        headerLogoUrl: toAbsoluteUrl(logoPath),
        headerLogoReady: apiState.loading ? false : prevConfig.headerLogoReady,
        source: apiState.data ? 'api' : prevConfig.source
      };
    });

    // Persist successful config from API
    if (shouldCallApi && !apiState.loading && apiState.data && !apiState.error) {
      try {
        localStorage.setItem('metro-config-cache', JSON.stringify({ 
          data: apiState.data, 
          docId: apiState.docId ?? docIdForConfig 
        }));
        if (getUserData()) {
          clearStaleCaches();
        }
      } catch {}
    }

    if (!apiState.loading && (apiState.data !== null || apiState.error !== null)) {
      setIsInitializing(false);
    }
  }, [apiState, docIdForConfig, isInitializing, metroConfig.isLoaded, shouldCallApi]);

  // Preload header logo image when URL becomes available
  useEffect(() => {
    const url = metroConfig.headerLogoUrl;
    if (!url) {
      // No custom logo; mark ready so UI can render with brand fallback
      if (!metroConfig.headerLogoReady) {
        setMetroConfig(prev => ({ ...prev, headerLogoReady: true }));
      }
      return;
    }
    // If already ready, skip
    if (metroConfig.headerLogoReady) return;

    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      setMetroConfig(prev => ({ ...prev, headerLogoReady: true }));
    };
    img.onerror = () => {
      if (cancelled) return;
      // Fallback: clear URL so header uses brand logo; allow render
      setMetroConfig(prev => ({ ...prev, headerLogoUrl: undefined, headerLogoReady: true }));
    };
    img.src = url;
    return () => {
      cancelled = true;
      // release handlers to avoid potential leaks
      img.onload = null;
      img.onerror = null;
      try { img.src = ''; } catch { /* noop */ }
    };
  }, [metroConfig.headerLogoUrl, metroConfig.headerLogoReady]);

  // If no docId, mark as initialized immediately -> add cleanup for timer
  useEffect(() => {
    if (!docIdForConfig) {
      const timer = window.setTimeout(() => setIsInitializing(false), 300);
      return () => window.clearTimeout(timer);
    }
  }, [docIdForConfig]);

  const clearMetroConfig = useCallback(() => {
    setMetroConfig({
      data: null,
      docId: undefined,
      isLoaded: false,
      loading: false,
      error: null,
      headerLogoReady: false,
      headerLogoUrl: undefined,
      source: 'initial'
    });
    clearMetroConfigCache();
    try { localStorage.removeItem('metro-config-cache'); } catch {}
  }, []);

  const retry = useCallback(() => {
    if (!docIdForConfig) return;
    clearMetroConfigCache(docIdForConfig);
    setIsInitializing(true);
  setMetroConfig(prev => ({ ...prev, loading: true, error: null, source: prev.source ?? 'initial' }));
    try { localStorage.removeItem('metro-config-cache'); } catch {}
  }, [docIdForConfig]);

  const contextValue = React.useMemo(() => ({
    metroConfig,
    setMetroConfig,
    clearMetroConfig,
    retry,
    isInitializing,
    docId: docIdForConfig
  }), [metroConfig, clearMetroConfig, retry, isInitializing, docIdForConfig]);

  return (
    <MetroConfigContext.Provider value={contextValue}>
      {children}
    </MetroConfigContext.Provider>
  );
};